import * as React from "react";
import { cn } from "../../lib/utils";

// Opt-in variants (NO-791). Each renders exactly like the default card until
// the always theme is on: every utility that differs reads a value only
// [data-theme="always"] defines, with the classic value as its fallback.
// Under the theme the hero card ("Happening now", "Up next") has a 2px olive
// edge and a lime offset shadow; the flag card (a red flag to act on) is rose
// with a 2px plum edge and a plum offset shadow.
const CARD_VARIANTS = {
  hero: "border-(length:--card-edge-width,1px) border-(color:--card-hero-edge,var(--border)) rounded-(--card-edge-radius,var(--radius-xl)) shadow-(--shadow-edge-lime,0_1px_2px_rgba(40,3,15,0.05))",
  flag: "border-(length:--card-edge-width,1px) border-(color:--card-flag-edge,var(--border)) bg-(--card-flag-bg,var(--card)) rounded-(--card-edge-radius,var(--radius-xl)) shadow-(--shadow-edge-plum,0_1px_2px_rgba(40,3,15,0.05))",
} as const;

export type CardVariant = "default" | keyof typeof CARD_VARIANTS;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "default", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-border bg-card text-card-foreground shadow-sm",
        variant === "default" ? undefined : CARD_VARIANTS[variant],
        className
      )}
      {...props}
    />
  )
);
Card.displayName = "Card";

export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1.5 p-6", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "font-sans text-base font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";
